import { Schema } from '@fincity/kirun-js';
import {
	SCHEMA_NUM_COMP_PROP,
	SCHEMA_BOOL_COMP_PROP,
	SCHEMA_STRING_COMP_PROP,
} from '../../constants';
import {
	ComponentPropertyDefinition,
	ComponentPropertyEditor,
	ComponentPropertyGroup,
} from '../../types/common';
import { COMMON_COMPONENT_PROPERTIES, COMPONENT_STYLE_GROUP_PROPERTIES } from '../util/properties';

const propertiesDefinition: Array<ComponentPropertyDefinition> = [
	// BASIC
	COMMON_COMPONENT_PROPERTIES.analyticsLabel,
	{
		name: 'showArrowButtons',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Left and right Buttons',
		description: 'Left and right arrow buttons to control carousel',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: true,
	},
	{
		name: 'showIndicators',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Show Indicators',
		description: 'Show indicator buttons for slides',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: true,
	},
	{
		name: 'showIndicatorArrows',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Show Indicator Arrows',
		description: 'Show navigation arrows in the indicator bar',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: true,
	},
	{
		name: 'indicatorPosition',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Indicator Position',
		description: 'Position of indicator buttons (bottom, top, left, right)',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 'bottom',
		enumValues: [
			{ name: 'bottom', displayName: 'Bottom', description: 'Indicators at the bottom' },
			{ name: 'top', displayName: 'Top', description: 'Indicators at the top' },
			{ name: 'left', displayName: 'Left', description: 'Indicators on the left' },
			{ name: 'right', displayName: 'Right', description: 'Indicators on the right' },
		],
	},
	{
		name: 'indicatorVisibleCount',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Visible Indicators Count',
		description: 'Number of indicators to show at once (windowed view)',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: -1,
	},
	{
		name: 'indicatorShape',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Indicator Shape',
		description: 'Shape of the indicator (circle, square, dash, none)',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 'circle',
		enumValues: [
			{ name: 'circle', displayName: 'Circle', description: 'Circle shape' },
			{ name: 'square', displayName: 'Square', description: 'Square shape' },
			{ name: 'dash', displayName: 'Dash', description: 'Dash shape' },
			{ name: 'none', displayName: 'None', description: 'No shape (numbers only)' },
		],
	},
	{
		name: 'indicatorFill',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Indicator Fill',
		description: 'Fill style of the indicator (outline, solid)',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 'outline',
		enumValues: [
			{ name: 'outline', displayName: 'Outline', description: 'Outline style' },
			{ name: 'solid', displayName: 'Solid', description: 'Solid style' },
		],
	},
	{
		name: 'indicatorShowNumbers',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Show Numbers in Indicators',
		description: 'Show slide numbers inside indicators',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: false,
	},
	{
		name: 'slideSpeed',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Carousel slide speed',
		description: `Adjust the slide speed by giving the time in milliseconds.`,
		group: ComponentPropertyGroup.BASIC,
		defaultValue: 2000,
	},
	{
		name: 'autoPlay',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Auto Play',
		description: 'Play automatically without interaction',
		group: ComponentPropertyGroup.BASIC,
		defaultValue: true,
	},

	// ADVANCED
	{
		name: 'animationType',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Animation Type',
		description: 'Animation Type',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.ADVANCED,
		defaultValue: 'fadeover',
		enumValues: [
			{ name: 'slide', displayName: 'Slide', description: 'Slide' },
			{ name: 'slideover', displayName: 'Slide Over', description: 'Slide Over' },
			{ name: 'fadeover', displayName: 'Fade Over', description: 'Fade Over' },
			{ name: 'fadeoutin', displayName: 'Fade Out In', description: 'Fade Out In' },
			{ name: 'crossover', displayName: 'Cross Over', description: 'Cross Over' },
		],
	},
	{
		name: 'animationDuration',
		schema: SCHEMA_NUM_COMP_PROP,
		displayName: 'Animation Duration',
		description: 'How long the animation should last.',
		group: ComponentPropertyGroup.ADVANCED,
		defaultValue: 2000,
	},
	{
		name: 'easing',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Easing',
		description: 'Easing',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.ADVANCED,
		defaultValue: 'ease',
		enumValues: [
			{ name: 'linear', displayName: 'Linear', description: 'Linear' },
			{ name: 'ease', displayName: 'Ease', description: 'Ease' },
			{ name: 'ease-in', displayName: 'Ease In', description: 'Ease In' },
			{ name: 'ease-out', displayName: 'Ease Out', description: 'Ease Out' },
			{ name: 'ease-in-out', displayName: 'Ease In Out', description: 'Ease In Out' },
			{ name: 'crossover', displayName: 'Cross Over', description: 'Cross Over' },
		],
	},
	{
		name: 'slideNavButtonPosition',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Slide Nav Button Position',
		description: 'Position of slide navigation buttons',
		editor: ComponentPropertyEditor.ENUM,
		group: ComponentPropertyGroup.ADVANCED,
		defaultValue: 'Bottom',
		enumValues: [
			{
				name: 'Top',
				displayName: 'Top',
				description: 'slide navigations button positioned on top',
			},
			{
				name: 'Right',
				displayName: 'Right',
				description: 'slide navigations button positioned on right',
			},
			{
				name: 'Bottom',
				displayName: 'Bottom',
				description: 'slide navigations button positioned on bottom',
			},
			{
				name: 'Left',
				displayName: 'Left',
				description: 'slide navigations button positioned on left',
			},
			{
				name: 'OutsideBottom',
				displayName: 'OutsideBottom',
				description: 'slide navigations button positioned on outside bottom',
			},
			{
				name: 'OutsideTop',
				displayName: 'OutsideTop',
				description: 'slide navigations button positioned on outside Top',
			},
		],
	},
	{
		name: 'arrowButtons',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Arrow Buttons',
		description: 'Position of arrow buttons',
		editor: ComponentPropertyEditor.ENUM,
		defaultValue: 'Middle',
		group: ComponentPropertyGroup.ADVANCED,
		enumValues: [
			{
				name: 'Middle',
				displayName: 'Center',
				description: 'arrow navigations button positioned on middle',
			},
			{
				name: 'LeftTop',
				displayName: 'top left',
				description: 'arrow navigations button positioned on top-Left',
			},
			{
				name: 'RightTop',
				displayName: 'top right',
				description: 'arrow navigations button positioned on top-right ',
			},
			{
				name: 'LeftBottom',
				displayName: 'bottom left',
				description: 'arrow navigations button positioned on bottom-left',
			},
			{
				name: 'RightBottom',
				displayName: 'bottom right',
				description: 'arrow navigations button positioned on  Bottom-right',
			},
			{
				name: 'OutsideBottomLeft',
				displayName: 'Outside-Bottom-Left',
				description: 'arrow navigations button positioned on outside-bottom-left',
			},
			{
				name: 'OutsideBottomRight',
				displayName: 'Outside-Bottom-Right',
				description: 'arrow navigations button positioned on outside-bottom-right',
			},
			{
				name: 'OutsideTopLeft',
				displayName: 'Outside-Top-Left',
				description: 'arrow navigations button positioned on outside-Top-left',
			},
			{
				name: 'OutsideTopRight',
				displayName: 'Outside-Top-Right',
				description: 'arrow navigations button positioned on outside-Top-right',
			},
		],
	},
	{
		name: 'showNavigationControlsOnHover',
		schema: SCHEMA_BOOL_COMP_PROP,
		displayName: 'Show navigation controls on hover',
		description:
			'This property display navigation controls, i.e; arrows and buttons, when the carousel is hovered upon.',
		group: ComponentPropertyGroup.ADVANCED,
		defaultValue: false,
	},

	// DATA
	{
		name: 'data',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Data',
		description: 'Data For Repeater',
		group: ComponentPropertyGroup.DATA,
	},
	{
		...COMMON_COMPONENT_PROPERTIES.datatype,
		enumValues: [
			{
				name: 'LIST_OF_STRINGS',
				displayName: 'List of strings',
				description: 'data has an array of strings',
			},
			{
				name: 'LIST_OF_OBJECTS',
				displayName: 'List of objects',
				description: 'data has an array of objects',
			},
		],
	},
	{
		name: 'selectionKey',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Selection Key',
		description: 'Selection Key',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'uniqueKey',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Unique Key',
		description: 'Unique Key',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'selectionType',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Selection Type',
		description: 'Selection Type',
		group: ComponentPropertyGroup.DATA,
	},
	{
		name: 'uniqueKeyType',
		schema: SCHEMA_STRING_COMP_PROP,
		displayName: 'Unique Key Type',
		description: 'Unique Key Type',
		group: ComponentPropertyGroup.DATA,
	},

	// COMMON
	COMMON_COMPONENT_PROPERTIES.visibility,
	COMMON_COMPONENT_PROPERTIES.readOnly,

	// DESIGN & COLOR
	{
		...COMMON_COMPONENT_PROPERTIES.designType,
	},
	COMMON_COMPONENT_PROPERTIES.colorScheme,
];

const stylePropertiesDefinition = {
	'': [
		COMPONENT_STYLE_GROUP_PROPERTIES.layout.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.position.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.border.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.background.type,
	],
	arrowButtonsContainer: [
		COMPONENT_STYLE_GROUP_PROPERTIES.layout.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.position.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.border.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.background.type,
	],
	arrowButtons: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
	],
	slideButtonsContainer: [
		COMPONENT_STYLE_GROUP_PROPERTIES.layout.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.position.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.border.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
	],
	dotButtons: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
	],
	indicatorContainer: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
	],
	indicatorButton: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
	],
	indicatorButtonActive: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.background.type,
	],
	indicatorNavBtn: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
	],
	indicatorNavBtnActive: [
		COMPONENT_STYLE_GROUP_PROPERTIES.spacing.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.typography.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.size.type,
		COMPONENT_STYLE_GROUP_PROPERTIES.effects.type,
	],
};

export { propertiesDefinition, stylePropertiesDefinition };
